import type { AcquisitionNumber, DetailedUser } from 'BrokerTsLib/src/dtos'
import { useGetAcquisitionNumbers } from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import React, { useEffect, useMemo } from 'react'

import { CustomWaypoint } from '@/components/CustomWaypoint'
import { PageSection } from '@/components/PageSection'
import { TableSection } from '@/components/Section/TableSection'
import { useSidebar } from '@/context/SidebarContext'
import type { PageSection as Section } from '@/layouts/components/Sidebar'

import { AcquisitionNumbersTable } from '../../components/AcquisitionNumbersTable'
import { PersonalInfo } from '../../components/User/PersonalInfo'

export const ProfileDetails = ({ user }: { user?: DetailedUser }) => {
  const { pageSections, setPageSections } = useSidebar()

  const { data: acquisitionNumbersData } = useGetAcquisitionNumbers(
    user?.organizationUnitId,
    undefined,
    {
      enabled: !!user,
    },
  )
  const acquisitionNumbers = useMemo(
    () => acquisitionNumbersData?.data ?? [],
    [acquisitionNumbersData],
  )

  useEffect(() => {
    const array: Section[] = []
    let i = 1

    if (user) {
      array.push({
        id: 'personalInfo',
        sectionTitle: 'Osobní informace',
        index: i++,
        children: (
          <CustomWaypoint sectionId="personalInfo">
            <PageSection id={'meetingRecord'}>
              <PersonalInfo user={user} />
            </PageSection>
          </CustomWaypoint>
        ),
      })

      const personalNumbersFiltered = [
        ...user.personalNumbers,
        ...user.personalNumbersProperty?.map((pnp) => ({
          ...pnp,
          insurerName: pnp.insurerName + ' (pojištění majetku)',
        })) ?? [],
      ].filter((pn) => pn.acquisitionNumber)

      if (personalNumbersFiltered.length > 0) {
        array.push({
          id: 'personalNumbers',
          sectionTitle: 'Osobní čísla',
          index: i++,
          children: (
            <CustomWaypoint sectionId="personalNumbers">
              <PageSection id={'personalNumbers'}>
                <PersonalNumbersSection
                  personalNumbers={personalNumbersFiltered}
                />
              </PageSection>
            </CustomWaypoint>
          ),
        })
      }
    }

    if (acquisitionNumbers) {
      array.push({
        id: 'acquisitionNumbers',
        sectionTitle: 'Ziskatelská čísla',
        index: i++,
        children: (
          <CustomWaypoint sectionId="acquisitionNumbers">
            <PageSection id={'acquisitionNumbers'}>
              <AcquisitionNumbersSection
                acquisitionNumbers={acquisitionNumbers}
              />
            </PageSection>
          </CustomWaypoint>
        ),
      })
    }

    setPageSections(array)

    return () => setPageSections([])
  }, [user, acquisitionNumbers, setPageSections])

  return pageSections.map((x) => (
    <React.Fragment key={x.id}>{x.children}</React.Fragment>
  ))
}

const PersonalNumbersSection = ({
  personalNumbers,
}: {
  personalNumbers: AcquisitionNumber[]
}) => {
  return (
    <TableSection sectionTitle={'Osobní čísla'}>
      <AcquisitionNumbersTable rows={personalNumbers} isDividedDisabled />
    </TableSection>
  )
}

const AcquisitionNumbersSection = ({
  acquisitionNumbers,
}: {
  acquisitionNumbers: AcquisitionNumber[]
}) => {
  return (
    <TableSection sectionTitle={'Ziskatelská čísla'}>
      <AcquisitionNumbersTable rows={acquisitionNumbers} />
    </TableSection>
  )
}
